import {
  CheckoutCancelledError,
  NOT_AVAILABLE,
  PAYMENT_FAILED,
  isSupportedProvider,
  toGatewayCallback,
  toRazorpayOptions,
} from './razorpayOptions';

/**
 * Opens the payment provider's checkout - web build (Razorpay checkout.js),
 * the same flow as the web portal's RazorpayCheckoutAdapter. Same signature as
 * the native razorpayCheckout.ts.
 *
 * checkout.js is loaded from Razorpay's CDN the first time it is needed (they
 * require it: the sheet talks to their servers) and the promise is cached; a
 * failed load clears the cache so a later attempt retries the fetch.
 */

const CHECKOUT_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const FAILED_TO_LOAD =
  'The payment window could not be loaded. Check the internet connection, or pay at the school office.';

type RazorpayConstructor = new (options: Record<string, unknown>) => {
  open: () => void;
  on: (event: string, handler: (payload: unknown) => void) => void;
};

let loader: Promise<RazorpayConstructor> | null = null;

// The app typechecks without the DOM lib (it is shared with native), so the
// browser globals are reached through globalThis, as documentFiles.web.ts does.
const win = () => (globalThis as any).window;
const doc = () => (globalThis as any).document;

function loadCheckoutScript(): Promise<RazorpayConstructor> {
  const w = win();
  const document = doc();
  if (!w || !document) {
    return Promise.reject(new Error('Razorpay Checkout needs a browser.'));
  }
  if (w.Razorpay) { return Promise.resolve(w.Razorpay as RazorpayConstructor); }
  if (loader) { return loader; }

  loader = new Promise<RazorpayConstructor>((resolve, reject) => {
    const onReady = () => {
      if (w.Razorpay) { resolve(w.Razorpay as RazorpayConstructor); }
      else { reject(new Error('The payment window loaded but did not start.')); }
    };
    const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', onReady, { once: true });
      existing.addEventListener('error', () => reject(new Error(FAILED_TO_LOAD)), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = CHECKOUT_SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', onReady, { once: true });
    script.addEventListener('error', () => reject(new Error(FAILED_TO_LOAD)), { once: true });
    document.body.appendChild(script);
  });

  loader = loader.catch(err => {
    loader = null;
    throw err;
  });
  return loader;
}

export async function openCheckout(
  provider: string,
  checkoutPayload: Record<string, string>,
  description?: string,
): Promise<Record<string, string>> {
  if (!isSupportedProvider(provider)) { throw new Error(NOT_AVAILABLE); }

  const Razorpay = await loadCheckoutScript();

  return new Promise<Record<string, string>>((resolve, reject) => {
    // After a successful payment Razorpay also raises dismiss as the sheet
    // closes; only the first outcome may settle.
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) { return; }
      settled = true;
      fn();
    };

    const checkout = new Razorpay({
      ...toRazorpayOptions(checkoutPayload, description),
      handler: (result: Record<string, string>) => finish(() => resolve(toGatewayCallback(result))),
      modal: {
        ondismiss: () => finish(() => reject(new CheckoutCancelledError())),
        escape: true,
      },
    });

    checkout.on('payment.failed', (event: unknown) => {
      const text = (event as { error?: { description?: string } })?.error?.description;
      finish(() => reject(new Error(text || PAYMENT_FAILED)));
    });

    checkout.open();
  });
}
